import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  Box,
  Alert,
  CircularProgress,
  IconButton,
} from '../../../ui/core';
import { CloseIcon } from '../../../ui/icons';
import { useTheme } from '@mui/material/styles';
import { updateRideLocation } from '../../../helpers/api/rides';
import { useToast } from '../../../hooks';

const makeInputStyle = (theme) => ({
  width: '100%',
  padding: '12px 14px',
  fontSize: '16px',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '4px',
  outline: 'none',
  boxSizing: 'border-box',
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
});

// The Google Places dropdown (.pac-container) renders at the document root and
// defaults to z-index ~1000, which is behind MUI Dialog (z-index 1300).
const pacStyle = document.createElement('style');
pacStyle.textContent = '.pac-container { z-index: 1500 !important; }';
document.head.appendChild(pacStyle);

const EditRideLocationDialog = ({ open, onClose, ride, tripId, kind, onSuccess }) => {
  const theme = useTheme();
  const inputStyle = makeInputStyle(theme);
  const rideId = ride.id || ride.rideId;
  const t = useToast();

  const [submitting, setSubmitting] = useState(false);
  const [warnings, setWarnings] = useState(null);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);

  // Reset state when dialog opens.
  useEffect(() => {
    if (open) {
      setWarnings(null);
      setSelectedPlace(null);
      setSubmitting(false);
    }
  }, [open]);

  // Attach Google Places Autocomplete after the dialog opens and input is in the DOM.
  useEffect(() => {
    if (!open) {
      if (autocompleteRef.current) {
        window.google?.maps?.event?.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
      return;
    }

    let cancelled = false;

    const tryAttach = () => {
      if (cancelled || autocompleteRef.current) return;

      if (!window.google?.maps?.places?.Autocomplete) {
        setTimeout(tryAttach, 500);
        return;
      }

      if (!inputRef.current) {
        setTimeout(tryAttach, 200);
        return;
      }

      const ac = new window.google.maps.places.Autocomplete(inputRef.current, {
        componentRestrictions: { country: 'us' },
        fields: ['formatted_address', 'name', 'geometry'],
      });

      ac.addListener('place_changed', () => {
        const place = ac.getPlace();
        if (!place?.geometry?.location) return;
        setSelectedPlace({
          address: place.formatted_address,
          name: place.name !== place.formatted_address ? place.name : undefined,
          coords: {
            lat: place.geometry.location.lat(),
            lon: place.geometry.location.lng(),
          },
        });
        setWarnings(null);
      });

      autocompleteRef.current = ac;
    };

    // Delay to let the dialog DOM render.
    const timer = setTimeout(tryAttach, 200);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      if (autocompleteRef.current) {
        window.google?.maps?.event?.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
    };
  }, [open]);

  const submit = async (overrideWarnings) => {
    if (!selectedPlace) return;
    setSubmitting(true);
    try {
      const response = await updateRideLocation(tripId, rideId, {
        kind,
        coords: selectedPlace.coords,
        address: selectedPlace.address,
        name: selectedPlace.name,
        overrideWarnings,
      });
      const result = response.data;
      if (!result.updated && result.warnings?.length > 0) {
        setWarnings(result.warnings);
        setSubmitting(false);
        return;
      }
      t.success(`${kind === 'pickup' ? 'Pick-up' : 'Drop-off'} location updated`);
      onSuccess?.();
      onClose();
    } catch (err) {
      const message =
        err.response?.data?.message || 'Failed to update location';
      t.error(message);
      setSubmitting(false);
    }
  };

  const currentWaypoint = kind === 'pickup' ? ride.pickUp : ride.dropOff;
  const currentAddress = currentWaypoint
    ? currentWaypoint.name || currentWaypoint.address
    : 'Unknown';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Box display="flex" alignItems="center" justifyContent="space-between" px="24px" pt="24px">
        <DialogTitle sx={{ padding: 0 }}>
          Edit {kind === 'pickup' ? 'Pick-up' : 'Drop-off'} Location
        </DialogTitle>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent>
        <Stack spacing="16px">
          <Box>
            <Typography variant="body2" color="text.secondary" mb="4px">
              Current {kind === 'pickup' ? 'pick-up' : 'drop-off'}
            </Typography>
            <Typography>{currentAddress}</Typography>
          </Box>

          <Box>
            <Typography variant="body2" color="text.secondary" mb="4px">
              New address
            </Typography>
            <input
              ref={inputRef}
              type="text"
              placeholder="Search for an address..."
              style={inputStyle}
            />
          </Box>

          {selectedPlace && (
            <Box>
              <Typography variant="body2" color="text.secondary">
                Selected: {selectedPlace.address}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                ({selectedPlace.coords.lat.toFixed(5)}, {selectedPlace.coords.lon.toFixed(5)})
              </Typography>
            </Box>
          )}

          {warnings && warnings.length > 0 && (
            <Stack spacing="8px">
              {warnings.map((w) => (
                <Alert key={w.code} severity="warning">
                  <Typography variant="body2" fontWeight={700}>{w.code}</Typography>
                  <Typography variant="body2">{w.message}</Typography>
                </Alert>
              ))}
            </Stack>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: '24px', pb: '24px' }}>
        <Button color="error" onClick={onClose}>
          Cancel
        </Button>
        {warnings ? (
          <Button
            variant="contained"
            color="warning"
            disabled={submitting}
            onClick={() => submit(true)}
          >
            {submitting ? <CircularProgress size={20} /> : 'Override warnings and apply'}
          </Button>
        ) : (
          <Button
            variant="contained"
            disabled={!selectedPlace || submitting}
            onClick={() => submit(false)}
          >
            {submitting ? <CircularProgress size={20} /> : 'Apply'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default EditRideLocationDialog;
